import ReCAPTCHA from 'react-google-recaptcha';
import { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';

// Views
import PatientCaptureView from './patientCaptureView';

// Stores
import { node as routeStore } from './domain';

// Helpers
import createPatientWithDependencies from './helpers/createPatientWithDependencies';

// Utils
import fileToBase64 from '@common/utils/fileToBase64';

// Constants
import store from 'bootstrap/store';
import common from '@clientCommon/app/bootstrap/store';
import { ResolutionText, RESOLUTION_TEXT_VISABILITY_TIMEOUT } from './constants';

// Types
import { Type as PatientImageType } from '@common/models/patientImages/constants';

const PatientCaptureContainer = () => {
  const captchaRef = useRef<ReCAPTCHA | null>(null);

  useEffect(() => {
    routeStore.locals.setInitialState();

    return () => {
      routeStore.locals.setInitialState();
    };
  }, []);

  const onSubmit = useCallback(async () => {
    if (
      !store.global.identifyingAccessTokens.identifyingAccessTokenId ||
      !routeStore.locals.patient.firstName ||
      !routeStore.locals.patient.lastName ||
      !routeStore.locals.patient.DOB ||
      !routeStore.locals.patient.zip
    ) {
      return;
    }

    if (routeStore.locals.hasPatientAlreadyBeenCaptured === null) {
      routeStore.locals.setIsCreatingPatient(true);
      const existingPatient = await common.models.patients.async.getCaptured(
        {
          firstName: routeStore.locals.patient.firstName,
          lastName: routeStore.locals.patient.lastName,
          DOB: routeStore.locals.patient.DOB,
          zip: routeStore.locals.patient.zip,
        },
        store.global.identifyingAccessTokens.identifyingAccessTokenId
      );
      routeStore.locals.setValue('hasPatientAlreadyBeenCaptured', !!existingPatient);
      if (existingPatient) {
        routeStore.locals.setValue('resolutionText', ResolutionText.Success);
        setTimeout(() => {
          routeStore.locals.setInitialState();
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
        }, RESOLUTION_TEXT_VISABILITY_TIMEOUT);
      }
      routeStore.locals.setIsCreatingPatient(false);
      return;
    }

    await createPatientWithDependencies();

    setTimeout(() => {
      if (routeStore.locals.resolutionText === ResolutionText.Success) {
        routeStore.locals.setInitialState();
      } else {
        routeStore.locals.setResolutionText(null);
      }

      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    }, RESOLUTION_TEXT_VISABILITY_TIMEOUT);
  }, []);

  const onChangeFile = useCallback(async (type: PatientImageType, files: FileList | null) => {
    if (!files || !files.length) {
      return;
    }

    const file: File = Array.from(files)[0];
    const base64File = await fileToBase64(file);
    if (typeof base64File === 'string') {
      routeStore.locals.setFile(type, {
        data: file,
        preview: base64File,
      });
    }
  }, []);

  return (
    <PatientCaptureView
      routeStore={routeStore.locals}
      captchaRef={captchaRef}
      onChangeFile={onChangeFile}
      onSubmit={onSubmit}
    />
  );
};

export default observer(PatientCaptureContainer);
